import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import RepeatIcon from '@mui/icons-material/Repeat';
import { makeStyles } from 'tss-react/mui';
import MaterialLink from './material-link';
import EventHost from './host';
import EventSeriesDescriptionPreview from './event-series-description-preview';
import EventSeriesNextEvent from './event-series-next-event';
import { eventSeriesScheduleSummary } from '../utils/event-series-schedule';
import { eventSeriesPath } from '../utils/slugify';
import type { EventSeries } from '../types/EventSeries';

const useStyles = makeStyles()((theme) => ({
  host: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(0.5, 0),
  },
  eventHost: {
    marginLeft: theme.spacing(1),
  },
  iconRow: {
    display: 'flex',
    alignItems: 'flex-start',
    margin: theme.spacing(0.5, 0),
    fontSize: theme.typography.body2.fontSize,
  },
  rowIcon: {
    marginRight: theme.spacing(0.5),
    color: theme.palette.text.primary,
  },
}));

type Props = {
  eventSeries: EventSeries;
};

const EventSeriesCard = ({ eventSeries }: Props) => {
  const { classes } = useStyles();
  const nextEvent = eventSeries.upcomingEvents?.[0];

  return (
    <Paper sx={{ p: 3, mb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="h3" sx={{ mr: 1, mt: 0, mb: 0 }}>
          <MaterialLink href={eventSeriesPath(eventSeries.id, eventSeries.name)}>
            {eventSeries.name}
          </MaterialLink>
        </Typography>
        {eventSeries.status === 'PAUSED' && (
          <Chip label="Paused" size="small" color="warning" />
        )}
        {eventSeries.status === 'CANCELLED' && (
          <Chip label="Cancelled" size="small" color="error" />
        )}
      </Box>

      <div className={classes.host}>
        Series by
        <EventHost event={eventSeries} className={classes.eventHost} />
      </div>

      {eventSeries.description && (
        <EventSeriesDescriptionPreview description={eventSeries.description} />
      )}

      <div className={classes.iconRow}>
        <RepeatIcon fontSize="small" className={classes.rowIcon} />
        {eventSeriesScheduleSummary(eventSeries)}
      </div>

      {nextEvent && <EventSeriesNextEvent nextEvent={nextEvent} />}

      <Box sx={{ mt: 1 }}>
        <Chip
          label={`${eventSeries.upcomingEventsCount} upcoming`}
          size="small"
          variant="outlined"
          color="primary"
        />
      </Box>
    </Paper>
  );
};

export default EventSeriesCard;
